<template>
  <div
    v-if="previewModel"
    class="flowbuilder-condition-preview"
  >
    <div
      v-if="conditionCount > 1"
      class="flowbuilder-condition-preview-header"
    >
      Any of these conditions:
    </div>

    <div class="flowbuilder-condition-preview-body">
      <PreviewNode
        :model="previewModel"
        :depth="0" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, defineComponent, h, shallowRef, watch } from 'vue'
import type { Component, PropType, VNodeChild } from 'vue'

type LogicLeaf = Record<string, any>
type LogicGroupOperator = 'and' | 'or'
type DescribeLogic = (logic: any, context?: any) => Promise<string | null>

interface DescriptionPart {
  text: string
  strong: boolean
}

interface PreviewLeaf {
  kind: 'leaf'
  parts: DescriptionPart[]
}

interface PreviewGroup {
  kind: 'group'
  operator: LogicGroupOperator
  label: string
  children: PreviewModel[]
}

type PreviewModel = PreviewLeaf | PreviewGroup

const props = defineProps<{
  condition: any
  describeLogic?: DescribeLogic
  context?: any
}>()

const previewModel = shallowRef<PreviewModel | null>(null)
const conditionCount = computed(() => previewModel.value ? countPreviewLeaves(previewModel.value) : 0)
let buildVersion = 0

const PreviewNode: Component = defineComponent({
  name: 'ConditionPreviewNode',
  props: {
    model: {
      type: Object as PropType<PreviewModel>,
      required: true
    },
    depth: {
      type: Number,
      required: true
    }
  },
  setup(nodeProps): () => VNodeChild {
    const rowClasses = computed(() => [
      'flowbuilder-condition-preview-row',
      nodeProps.depth > 0 ? 'flowbuilder-condition-preview-row--nested' : ''
    ])

    const groupClasses = computed(() => [
      'flowbuilder-condition-preview-group',
      nodeProps.depth > 0
        ? 'flowbuilder-condition-preview-group--nested'
        : ''
    ])

    return (): VNodeChild => {
      if (nodeProps.model.kind === 'leaf') {
        return h('div', { class: rowClasses.value }, [
          h('span', {
            class: 'flowbuilder-condition-preview-dot'
          }),
          h('span', { class: 'min-w-0' }, renderDescriptionParts(nodeProps.model.parts))
        ])
      }

      const children = h('div', { class: 'space-y-2' }, nodeProps.model.children.map((child, index): VNodeChild => h(
        'div',
        {
          key: index,
          class: index === 0
            ? ''
            : 'flowbuilder-condition-preview-divider'
        },
        [h(PreviewNode, { model: child, depth: nodeProps.depth + 1 })]
      )))

      return h('div', { class: groupClasses.value }, [children])
    }
  }
})

function renderDescriptionParts(parts: DescriptionPart[]): VNodeChild[] {
  return parts.map((part, index) => h(
    part.strong ? 'strong' : 'span',
    {
      key: index,
      class: part.strong ? 'flowbuilder-condition-preview-strong' : undefined
    },
    part.text
  ))
}

function isLogicObject(value: any): value is Record<string, any> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function groupOperator(value: any): LogicGroupOperator | null {
  if (!isLogicObject(value)) return null
  if (Array.isArray(value.and)) return 'and'
  if (Array.isArray(value.or)) return 'or'
  return null
}

function groupItems(value: Record<string, any>, operator: LogicGroupOperator) {
  return value[operator] as any[]
}

function isRealLeaf(value: any): value is LogicLeaf {
  if (!isLogicObject(value) || groupOperator(value)) return false
  return value.$type !== undefined && String(value.$type).length > 0
}

function groupLabel(operator: LogicGroupOperator, children: PreviewModel[]) {
  if (operator === 'and') return 'All of these conditions'
  return children.some((child) => child.kind === 'group')
    ? 'Any of these groups'
    : 'Any of these conditions'
}

function countPreviewLeaves(model: PreviewModel): number {
  if (model.kind === 'leaf') return 1
  return model.children.reduce((count, child) => count + countPreviewLeaves(child), 0)
}

function parseDescription(description: string): DescriptionPart[] {
  const parts: DescriptionPart[] = []
  const matcher = /\*\*(.*?)\*\*/g
  let lastIndex = 0
  let match: RegExpExecArray | null

  while ((match = matcher.exec(description)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ text: description.slice(lastIndex, match.index), strong: false })
    }

    if (match[1]) {
      parts.push({ text: match[1], strong: true })
    }

    lastIndex = matcher.lastIndex
  }

  if (lastIndex < description.length) {
    parts.push({ text: description.slice(lastIndex), strong: false })
  }

  return parts.length > 0 ? parts : [{ text: description, strong: false }]
}

async function describeLeaf(leaf: LogicLeaf) {
  if (!props.describeLogic) return 'Condition configured'

  try {
    const description = await props.describeLogic(
      { or: [{ and: [leaf] }] },
      props.context
    )

    return description || 'Condition configured'
  } catch {
    return 'Condition configured'
  }
}

async function buildPreviewNode(value: any): Promise<PreviewModel | null> {
  const operator = groupOperator(value)

  if (operator) {
    const children = (await Promise.all(
      groupItems(value, operator).map((item) => buildPreviewNode(item))
    )).filter((child): child is PreviewModel => child !== null)

    if (children.length === 0) return null
    if (children.length === 1) return children[0]

    return {
      kind: 'group',
      operator,
      label: groupLabel(operator, children),
      children
    }
  }

  if (!isRealLeaf(value)) return null

  return {
    kind: 'leaf',
    parts: parseDescription(await describeLeaf(value))
  }
}

async function rebuildPreviewModel() {
  const version = ++buildVersion
  const model = await buildPreviewNode(props.condition)

  if (version === buildVersion) {
    previewModel.value = model
  }
}

watch(
  () => ({
    condition: props.condition,
    context: props.context,
    describeLogic: props.describeLogic
  }),
  () => {
    rebuildPreviewModel()
  },
  { deep: true, immediate: true }
)
</script>
